import { OpenPosition, UIOrderType } from '@velocity-exchange/common';
import { BN, ZERO } from '@velocity-exchange/sdk';
import { useEffect, useMemo } from 'react';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { getShouldShowSecondaryPriceBox } from 'src/utils/trade';
import usePreviewOrder from './usePreviewOrder';

const ALLOWED_ORDER_TYPES_FOR_POST_ONLY: UIOrderType[] = [
	'limit',
	'oracleLimit',
	'scaledOrders',
];

const useIsReduceOnlyDisabled = (
	currentOrderType: UIOrderType,
	tradeFormSide: 'buy' | 'sell',
	currentPosition: OpenPosition | undefined,
	currentPositionDirection: 'long' | 'short'
) => {
	const isMarketOrder = currentOrderType === 'market';
	const hasZeroPosition = !currentPosition || currentPosition.baseSize.eq(ZERO);
	const isTradeFormSideSameAsPosition =
		tradeFormSide === 'buy'
			? currentPositionDirection === 'long'
			: currentPositionDirection === 'short';

	const isMarketOrderInSameDirection =
		(hasZeroPosition || isTradeFormSideSameAsPosition) && isMarketOrder;

	return isMarketOrderInSameDirection;
};

/**
 * Displays the postOnly control if the current order type is allowed for post only.
 */
const useShowPostOnlyControl = (currentOrderType: UIOrderType) => {
	const setVelocityStore = useVelocityStore((s) => s.set);

	const showPostOnlyControl = useMemo(
		() => ALLOWED_ORDER_TYPES_FOR_POST_ONLY.includes(currentOrderType),
		[currentOrderType]
	);

	// When order type is not allowed for post only, set the postOnly flag to false
	useEffect(() => {
		if (!showPostOnlyControl) {
			setVelocityStore((s) => {
				s.tradeForm.postOnly = false;
			});
		}
	}, [showPostOnlyControl, setVelocityStore]);

	return showPostOnlyControl;
};

/**
 * Syncs the reduce only state and trade form side with the current position and order type.
 * Updates the base size to the max valid reduce only value if the base size input is greater
 * than the current position. This only occurs when the order type changes or the current position
 * updates.
 */
const useSyncReduceOnlyState = (
	currentOrderType: UIOrderType,
	currentPositionDirection: 'long' | 'short',
	currentPositionBaseSize: BN | undefined,
	baseSizeInputBN: BN,
	isReduceOnlyDisabled: boolean
) => {
	const setVelocityStore = useVelocityStore((s) => s.set);

	// If reduce only is disabled, set reduce only to false
	useEffect(() => {
		if (isReduceOnlyDisabled) {
			setVelocityStore((s) => {
				s.tradeForm.reduceOnly = false;
			});
		}
	}, [isReduceOnlyDisabled, setVelocityStore]);
};

const useSyncTpSlCheckbox = (tradeFormIsReduceOnly: boolean) => {
	const setVelocityStore = useVelocityStore((s) => s.set);

	useEffect(() => {
		if (tradeFormIsReduceOnly) {
			setVelocityStore((s) => {
				s.tradeForm.isTpSlCheckboxChecked = false;
			});
		}
	}, [tradeFormIsReduceOnly]);
};

const useShowPriceEstimateOracleDivergenceWarning = (
	currentOrderType: UIOrderType
) => {
	const previewOrderParams = usePreviewOrder();

	// Use the estimated-entry-price divergence (matches the warning copy and the
	// spot form), NOT `isDivergentFromOracle` which measures the slippage-adjusted
	// auction end price and fires whenever slippage tolerance exceeds the threshold.
	const showOracleDivergenceWarning =
		previewOrderParams?.priceImpact?.showPriceEstimateOracleDivergenceWarning ??
		false;

	const showPriceEstimateOracleDivergenceWarning = Boolean(
		showOracleDivergenceWarning && currentOrderType === 'market'
	);

	return showPriceEstimateOracleDivergenceWarning;
};

export const useSyncOrderTypeControls = (
	currentOrderType: UIOrderType,
	currentPositionDirection: 'long' | 'short',
	currentPosition: OpenPosition | undefined,
	baseSizeInputBN: BN,
	tradeFormSide: 'buy' | 'sell',
	tradeFormIsReduceOnly: boolean
) => {
	const isReduceOnlyDisabled = useIsReduceOnlyDisabled(
		currentOrderType,
		tradeFormSide,
		currentPosition,
		currentPositionDirection
	);

	useSyncReduceOnlyState(
		currentOrderType,
		currentPositionDirection,
		currentPosition?.baseSize,
		baseSizeInputBN,
		isReduceOnlyDisabled
	);

	useSyncTpSlCheckbox(tradeFormIsReduceOnly);

	const showPostOnlyControl = useShowPostOnlyControl(currentOrderType);

	const showReducePositionSlider = useMemo(() => {
		if (tradeFormIsReduceOnly) {
			const hasNonZeroPosition =
				currentPosition && !currentPosition.baseSize.eq(ZERO);
			const isTradeFormSideOppositeOfPosition =
				tradeFormSide === 'buy'
					? currentPositionDirection === 'short'
					: currentPositionDirection === 'long';

			return hasNonZeroPosition && isTradeFormSideOppositeOfPosition;
		} else {
			return false;
		}
	}, [
		tradeFormIsReduceOnly,
		currentPosition?.baseSize,
		tradeFormSide,
		currentPositionDirection,
	]);

	const showLeverageSlider = !tradeFormIsReduceOnly;

	const showSecondaryPriceBox =
		getShouldShowSecondaryPriceBox(currentOrderType);

	const showSlippageTolerance = currentOrderType === 'market';

	const showOrdersSplit = currentOrderType === 'scaledOrders';

	const showPriceEstimateOracleDivergenceWarning =
		useShowPriceEstimateOracleDivergenceWarning(currentOrderType);

	return {
		isReduceOnlyDisabled,
		showPostOnlyControl,
		showReducePositionSlider,
		showLeverageSlider,
		showSecondaryPriceBox,
		showSlippageTolerance,
		showOrdersSplit,
		showPriceEstimateOracleDivergenceWarning,
	};
};
